import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import BlockReasons from '~/compositions/LockCard/Steps/BlockReasons';
import dashboardColors from '~/helpers/dashboardColors';
import { Button as ButtonElement } from '~/elements';
import { Loader } from '~/components';
import { cards } from '~/helpers/api';
import messages from '~/helpers/api/messages';
import { ToastCtx } from '~/providers/ToastProvider';
import ConfirmBlock from '~/compositions/LockCard/Steps/ConfirmBlock';
import getLastFourDigits from '~/helpers/getLastFourDigits';
import { VRPAT } from '~/config/constants';
import CurrentCardNumber from '../ChangeCardPassword/Steps/CurrentCardNumber';

const Button = styled(ButtonElement)`
  margin-top: 40px !important;
`;
const Container = styled.form`
  display: flex;
  align-items: center;
  background-color: ${({ theme, activeCardCode }) =>
    dashboardColors(theme, activeCardCode).bgModal};
  color: ${({ theme, activeCardCode }) =>
    dashboardColors(theme, activeCardCode).colorModal};
  padding: 15px 0;

  h1 ~ p,
  .MuiFormControlLabel-label,
  .MuiRadio-colorSecondary {
    color: ${({ theme, activeCardCode }) =>
      dashboardColors(theme, activeCardCode).colorModal} !important;
  }

  @media (min-width: ${({ theme }) => theme.devices.mobile}) {
    padding: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 15px;

  button {
    width: 100%;
  }

  @media (min-width: ${({ theme }) => theme.devices.mobile}) {
    padding: 70px 0;

    * {
      max-width: 333px;
    }
  }
`;

export default function LockCard({ currentStep, setStep, updateCards }) {
  const toast = useContext(ToastCtx);
  const [isLoading, setIsloading] = useState(false);

  const activeCard = useSelector(state => state.home.cards.active);
  const [reasonList, setReasonList] = useState([]);
  const [selectedCode, setSelectedCode] = useState();
  const issuer = useSelector(state => state?.issuer?.name);
  const color = issuer === VRPAT ? 'secondary' : 'caixa';

  const { getValues, errors, ...formControl } = useForm({
    mode: 'onChange',
  });

  async function getBlockReasons() {
    try {
      setIsloading(true);
      const data = await cards.lockReasons();
      setReasonList(data?.result);
    } catch (error) {
      toast.dispatch(messages(error), 'error');
    } finally {
      setIsloading(false);
    }
  }

  useEffect(() => {
    getBlockReasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function lockCard() {
    try {
      setIsloading(true);

      const payload = {
        code: selectedCode,
        cnpjRH: activeCard?.cnpjRH,
        numberCard: getValues('cardNumberChangingPassword').replace(/\s+/g, ''),
      };

      const response = await cards.lockCard(payload);
      await updateCards();
      toast.dispatch(messages(response, 'lock_card'), 'success');
    } catch (error) {
      toast.dispatch(messages(error, 'lock_card'), 'error');
    } finally {
      setStep('');
      setIsloading(false);
    }
  }

  function handleCurrentStep() {
    let component;

    if (currentStep === 'block_reasons') {
      component = (
        <CurrentCardNumber formControl={formControl} errors={errors} />
      );
    }
    if (currentStep === 'block_reasons2') {
      component = (
        <BlockReasons
          reasonList={reasonList}
          selectedCode={code => setSelectedCode(code)}
        />
      );
    }
    if (currentStep === 'confirm_block') {
      component = (
        <ConfirmBlock cardNumber={getLastFourDigits(activeCard?.numberCard)} />
      );
    }

    return component;
  }

  function handleButton() {
    let stepName = '';
    let label = '';
    let onClick;
    if (currentStep === 'block_reasons') {
      stepName = 'block_reasons2';
      label = 'Continuar';
      onClick = () => setStep(stepName);
    }
    if (currentStep === 'block_reasons2') {
      stepName = 'confirm_block';
      label = 'Continuar';
      onClick = () => setStep(stepName);
    }

    if (currentStep === 'confirm_block') {
      onClick = () => lockCard();
      label = 'Bloquear e solicitar segunda via';
    }

    return (
      <Button
        aria-label={label}
        disabled={
          !selectedCode && getValues('cardNumberChangingPassword')?.length < 22
        }
        onClick={onClick}
        label={label}
        color={color}
      />
    );
  }

  return (
    <Container
      activeCardCode={activeCard?.tipo ? 'caixa' : activeCard?.codigoProduto}
    >
      <Loader isLoading={isLoading}>
        <Content>
          {handleCurrentStep()}
          {handleButton()}
        </Content>
      </Loader>
    </Container>
  );
}

LockCard.propTypes = {
  currentStep: PropTypes.string,
  setStep: PropTypes.func,
  updateCards: PropTypes.func,
};
